import React, { useState, useEffect, useMemo } from "react";
import { Loader, Pagination, Dropdown, Input, Button } from "semantic-ui-react";
import { size, map } from "lodash";
import { SceneItems } from "../SceneItems";
import { VirtualScenes } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { categoryOptions, subcategoryOptions } from "../SceneForm/StepOne.form";
import "./SceneList.scss";

const virtualSceneController = new VirtualScenes();

export function SceneList(props) {
  const { reload, onReload } = props;
  const [scenes, setScenes] = useState(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const { accessToken } = useAuth();

  // Filtros
  const [filterTitle, setFilterTitle] = useState("");
  const [filterCategory, setFilterCategory] = useState("Todas");
  const [filterSubcategory, setFilterSubcategory] = useState("Todas");
  const [filterAccount, setFilterAccount] = useState("Todas");

  // Recarga datos al cambiar página o reload
  useEffect(() => {
    (async () => {
      try {
        const response = await virtualSceneController.getVirtualScenesMini(
          { page, limit: 300 },
          accessToken,
        );
        setScenes(response.miniatureData);

        setPagination({
          limit: response.limit,
          page: response.page,
          totalPages: response.totalPages,
          totalDocs: response.totalDocs,
        });
      } catch (error) {
        console.error("Error al cargar las escenas:", error);
      }
    })();
  }, [page, reload, accessToken]);

  // Cambiar página
  const changePage = (e, data) => {
    setPage(data.activePage);
  };

  // Opciones fijas para dropdowns, añadiendo "Todas"
  const allCategoryOptions = useMemo(
    () => [{ key: "Todas", text: "Todas", value: "Todas" }, ...categoryOptions],
    [],
  );
  const allSubcategoryOptions = useMemo(
    () => [
      { key: "Todas", text: "Todas", value: "Todas" },
      ...subcategoryOptions,
    ],
    [],
  );
  const accountOptions = [
    { key: "Todas", text: "Todas", value: "Todas" },
    { key: "free", text: "Free", value: "free" },
    { key: "premium", text: "Premium", value: "premium" },
    { key: "creator", text: "Creator", value: "creator" },
  ];

  // Filtrar escenas
  const filteredScenes = useMemo(() => {
    if (!scenes) return [];
    return scenes.filter((scene) => {
      const matchesTitle = filterTitle
        ? scene.title.toLowerCase().includes(filterTitle.toLowerCase())
        : true;
      const matchesCategory =
        filterCategory === "Todas"
          ? true
          : (scene.category || []).includes(filterCategory);
      const matchesSubcategory =
        filterSubcategory === "Todas"
          ? true
          : (scene.subcategory || []).includes(filterSubcategory);
      const matchesAccount =
        filterAccount === "Todas" ? true : scene.account === filterAccount;

      return (
        matchesTitle && matchesCategory && matchesSubcategory && matchesAccount
      );
    });
  }, [scenes, filterTitle, filterCategory, filterSubcategory, filterAccount]);

  const handleResetFilters = () => {
    setFilterTitle("");
    setFilterCategory("Todas");
    setFilterSubcategory("Todas");
    setFilterAccount("Todas");
  };

  // Render
  if (!scenes) return <Loader active inline="centered" />;
  if (size(filteredScenes) === 0)
    return (
      <div className="scene-list-container">
        <SceneListFilters
          {...{
            filterTitle,
            setFilterTitle,
            filterCategory,
            setFilterCategory,
            categoryOptions: allCategoryOptions,
            filterSubcategory,
            setFilterSubcategory,
            subcategoryOptions: allSubcategoryOptions,
            filterAccount,
            setFilterAccount,
            accountOptions,
            handleResetFilters,
          }}
        />
        <h2>No hay escenas</h2>
      </div>
    );

  return (
    <div className="scene-list-container">
      <SceneListFilters
        {...{
          filterTitle,
          setFilterTitle,
          filterCategory,
          setFilterCategory,
          categoryOptions: allCategoryOptions,
          filterSubcategory,
          setFilterSubcategory,
          subcategoryOptions: allSubcategoryOptions,
          filterAccount,
          setFilterAccount,
          accountOptions,
          handleResetFilters,
        }}
      />

      <div className="list-scenes">
        {map(filteredScenes, (scene) => (
          <SceneItems key={scene.refId} scene={scene} onReload={onReload} />
        ))}
      </div>

      {pagination && (
        <div className="pagination-container">
          <Pagination
            totalPages={pagination.totalPages}
            activePage={pagination.page}
            onPageChange={changePage}
            ellipsisItem={null}
            firstItem={null}
            lastItem={null}
          />
        </div>
      )}
    </div>
  );
}

// --- Componente de filtros ---
function SceneListFilters({
  filterTitle,
  setFilterTitle,
  filterCategory,
  setFilterCategory,
  categoryOptions,
  filterSubcategory,
  setFilterSubcategory,
  subcategoryOptions,
  filterAccount,
  setFilterAccount,
  accountOptions,
  handleResetFilters,
}) {
  return (
    <div
      className="scene-list-filters"
      style={{
        display: "flex",
        gap: "1rem",
        marginBottom: "1.5rem",
        alignItems: "center",
        flexWrap: "wrap",
      }}
    >
      <Input
        placeholder="Buscar por título..."
        value={filterTitle}
        onChange={(_, data) => setFilterTitle(data.value)}
        style={{ minWidth: 200 }}
      />
      <Dropdown
        placeholder="Categoría"
        selection
        clearable={false}
        options={categoryOptions}
        value={filterCategory}
        onChange={(_, data) => setFilterCategory(data.value)}
        style={{ minWidth: 180 }}
      />
      <Dropdown
        placeholder="Subcategoría"
        selection
        clearable={false}
        options={subcategoryOptions}
        value={filterSubcategory}
        onChange={(_, data) => setFilterSubcategory(data.value)}
        style={{ minWidth: 180 }}
      />
      <Dropdown
        placeholder="Tipo de cuenta"
        selection
        clearable={false}
        options={accountOptions}
        value={filterAccount}
        onChange={(_, data) => setFilterAccount(data.value)}
        style={{ minWidth: 140 }}
      />
      <Button icon="undo" content="Todas" onClick={handleResetFilters} basic />
    </div>
  );
}
